import { Add } from "@mui/icons-material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ButtonBase, Menu, MenuItem } from "@mui/material";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { useRoleController } from "@/components/useRoleController";
import {
  CREATE_ACTION_ICONS,
  CREATE_ACTION_LABELS,
  CREATE_ACTION_PERMISSIONS,
  CREATE_ACTIONS,
  NAV_BAR_COLORS,
  NAV_BAR_LABELS,
  NAV_BAR_LAYOUT,
  NAV_BAR_POPUP_IDS,
  NAV_BAR_TYPOGRAPHY,
} from "../nav-bar-constant";
import type { CreateAction } from "../nav-bar-types";

export interface NavBarCreateMenuProps {
  onSelect: (action: CreateAction) => void;
}

const expandIcon = (isOpen: boolean) => {
  const Icon = isOpen ? ExpandLessIcon : ExpandMoreIcon;
  return (
    <Icon color="primary" sx={{ fontSize: NAV_BAR_LAYOUT.CREATE_ICON_SIZE }} />
  );
};

const NavBarCreateMenu = ({ onSelect }: NavBarCreateMenuProps) => {
  const { hasUpdate: canCreatePatient } = useRoleController(
    CREATE_ACTION_PERMISSIONS[CREATE_ACTIONS.PATIENT]
  );
  const { hasUpdate: canCreateAppointment } = useRoleController(
    CREATE_ACTION_PERMISSIONS[CREATE_ACTIONS.APPOINTMENT]
  );
  const { hasUpdate: canCreateInstantAppointment } = useRoleController(
    CREATE_ACTION_PERMISSIONS[CREATE_ACTIONS.INSTANT_APPOINTMENT]
  );

  const permissions: Record<CreateAction, boolean> = {
    [CREATE_ACTIONS.PATIENT]: canCreatePatient,
    [CREATE_ACTIONS.APPOINTMENT]: canCreateAppointment,
    [CREATE_ACTIONS.INSTANT_APPOINTMENT]: canCreateInstantAppointment,
  };

  const allowedActions = (
    Object.keys(CREATE_ACTION_LABELS) as CreateAction[]
  ).filter((action) => permissions[action]);

  if (!allowedActions.length) return null;

  return (
    <PopupState variant="popover" popupId={NAV_BAR_POPUP_IDS.CREATE_MENU}>
      {(popupState) => (
        <>
          <ButtonBase
            sx={{
              gap: NAV_BAR_LAYOUT.CREATE_GAP,
              height: NAV_BAR_LAYOUT.CREATE_HEIGHT,
              px: NAV_BAR_LAYOUT.CREATE_PADDING_X,
              borderRadius: NAV_BAR_LAYOUT.CREATE_RADIUS,
              backgroundColor: NAV_BAR_COLORS.SURFACE,
            }}
            {...bindTrigger(popupState)}
          >
            <Add
              color="primary"
              sx={{ fontSize: NAV_BAR_LAYOUT.CREATE_ICON_SIZE }}
            />
            <Text
              fontSize={NAV_BAR_TYPOGRAPHY.LABEL_FONT_SIZE}
              fontWeight={NAV_BAR_TYPOGRAPHY.LABEL_FONT_WEIGHT}
              c="primary.main"
              noWrap
            >
              {NAV_BAR_LABELS.CREATE}
            </Text>
            {expandIcon(popupState.isOpen)}
          </ButtonBase>

          <Menu {...bindMenu(popupState)}>
            {allowedActions.map((action) => {
              const ActionIcon = CREATE_ACTION_ICONS[action];
              return (
                <MenuItem
                  key={action}
                  onClick={() => {
                    onSelect(action);
                    popupState.close();
                  }}
                >
                  <Flex gap={1.25} alignItems="center">
                    <ActionIcon
                      sx={{ fontSize: NAV_BAR_LAYOUT.TAB_ICON_SIZE }}
                    />
                    <Text
                      fontSize={NAV_BAR_TYPOGRAPHY.LABEL_FONT_SIZE}
                      c={NAV_BAR_COLORS.MENU_ITEM_TEXT}
                    >
                      {CREATE_ACTION_LABELS[action]}
                    </Text>
                  </Flex>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </PopupState>
  );
};

export default NavBarCreateMenu;
